import exportFromJSON from "export-from-json";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Col, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import { deleteSliderAPI, getAllSlidersAPI, updateSliderAPI } from "../../../../api/slider.service.ts";
import {
    BlockBetween,
    BlockContent,
    BlockDes,
    BlockHead,
    BlockHeadContent,
    BlockTitle,
    Icon,
    PreviewCard,
    ReactDataTable,
} from "../../../../components/Component";
import DeleteModal from "../../../../components/DeleteModal";
import Content from "../../../../layout/content/Content";
import Head from "../../../../layout/head/Head";

const SliderList = () => {
    const navigate = useNavigate();
    const [sliders, setSliders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isSequenceModalOpen, setIsSequenceModalOpen] = useState(false);
    const [selectedSlider, setSelectedSlider] = useState(null);
    const [sequenceInput, setSequenceInput] = useState(0);

    const toggleDeleteModal = () => setIsDeleteModalOpen(!isDeleteModalOpen);
    const toggleSequenceModal = () => setIsSequenceModalOpen(!isSequenceModalOpen);

    const handleDelete = async (slider) => {
        try {
            await deleteSliderAPI({ id: slider._id });
            setSliders((prev) => prev.filter((s) => s._id !== slider._id));
            toast.success("Slider deleted successfully", { position: "top-center" });
            toggleDeleteModal();
        } catch (error) {
            toast.error(`Failed to delete slider: ${error.message}`, { position: "top-center" });
        }
    };

    const handleSliderSequence = async (sliderId, sequence) => {
        try {
            const formData = new FormData();
            formData.append("data", JSON.stringify({ sequence: Number(sequence) }));
            await updateSliderAPI({ id: sliderId, formData });
            setSliders((prev) =>
                prev.map((s) => (s._id === sliderId ? { ...s, sequence: Number(sequence) } : s))
            );
            toast.success("Slider sequence updated successfully", { position: "top-center" });
            toggleSequenceModal();
        } catch (error) {
            toast.error(`Failed to update slider sequence: ${error.message}`, { position: "top-center" });
        }
    };

    const openSequenceModal = (slider) => {
        setSelectedSlider(slider);
        setSequenceInput(slider.sequence || 0);
        toggleSequenceModal();
    };

    useEffect(() => {
        const fetchSliders = async () => {
            try {
                setLoading(true);
                const response = await getAllSlidersAPI();
                const fetchedSliders = response.data.data;
                setSliders(fetchedSliders?.reverse());
            } catch (error) {
                console.error("Failed to fetch sliders:", error);
                toast.error("Failed to fetch sliders", { position: "top-center" });
            } finally {
                setLoading(false);
            }
        };

        fetchSliders();
    }, []);

    const exportData = (type) => {
        const fileName = "sliders";
        const exportType = type === "csv" ? exportFromJSON.types.csv : exportFromJSON.types.xls;
        const exportData = sliders.map((slider) => ({
            Title: slider.title,
            Subtitle: slider.subtitle,
            "Button Label": slider.button.label,
            "Button URL": slider.button.actionURL,
            Sequence: slider.sequence || 0,
            Hidden: slider.isHidden ? "Yes" : "No",
            "Image URL": slider.imageUrl || "N/A",
        }));
        exportFromJSON({ data: exportData, fileName, exportType });
    };

    const columns = [
        {
            name: "Image",
            selector: (row) => row.imageUrl,
            cell: (row) => (
                <div className="d-flex align-items-center m-1 gap-2" style={{ cursor: "pointer" }}>
                    <img
                        src={row.imageUrl || "https://via.placeholder.com/50"}
                        alt={row.title}
                        className="img-fluid rounded py-1"
                        style={{ maxWidth: "50px", maxHeight: "50px", objectFit: "cover" }}
                        onError={(e) => (e.currentTarget.src = "https://via.placeholder.com/50")}
                    />
                    <div
                        className="fw-bold text-truncate"
                        style={{ cursor: "pointer", width: "80px" }}
                        onClick={() => navigate(`/edit-slider/${row._id}`)}
                    >
                        {row.title}
                    </div>
                </div>
            ),
        },
        {
            name: "Title",
            selector: (row) => row.title,
            cell: (row) => <span className="text-muted">{row.title}</span>,
            sortable: true,
        },
        {
            name: "Subtitle",
            selector: (row) => row.subtitle,
            cell: (row) => <span className="text-muted">{row.subtitle}</span>,
            sortable: true,
            hide: "md",
        },
        {
            name: "Button Label",
            selector: (row) => row.button.label,
            cell: (row) => <span className="text-muted">{row.button.label}</span>,
            sortable: true,
            hide: "md",
        },
        {
            name: "Button URL",
            selector: (row) => row.button.actionURL,
            cell: (row) => (
                <a href={row.button.actionURL} target="_blank" rel="noopener noreferrer" className="text-primary">
                    {row.button.actionURL}
                </a>
            ),
            sortable: true,
            hide: "lg",
        },
        {
            name: "Status",
            selector: (row) => row.isHidden,
            cell: (row) => (
                <span
                    className={`badge rounded-pill ${row.isHidden ? "bg-danger text-white" : "bg-success text-white"}`}
                >
                    {row.isHidden ? "Hidden" : "Visible"}
                </span>
            ),
            sortable: true,
        },
        {
            name: "Sequence",
            selector: (row) => row.sequence || 0,
            cell: (row) => (
                <div className="d-flex align-items-center gap-2">
                    <span>{row.sequence || 0}</span>
                    <Button
                        size="sm"
                        color="outline-primary"
                        onClick={() => openSequenceModal(row)}
                    >
                        <Icon name="sort"></Icon>
                    </Button>
                </div>
            ),
            sortable: true,
        },
        {
            name: "Actions",
            cell: (row) => (
                <div className="d-flex align-items-center gap-2">
                    <Button
                        size="sm"
                        color="outline-primary"
                        onClick={() => navigate(`/edit-slider/${row._id}`)}
                    >
                        <i className="bi bi-pencil-fill me-1"></i>
                        <span className="d-none d-md-inline">Edit</span>
                    </Button>
                    <Button
                        size="sm"
                        color="outline-danger"
                        onClick={() => {
                            setSelectedSlider(row);
                            toggleDeleteModal();
                        }}
                    >
                        <i className="bi bi-trash-fill me-1"></i>
                        <span className="d-none d-md-inline">Delete</span>
                    </Button>
                </div>
            ),
            ignoreRowClick: true,
        },
    ];

    const expandableRowsComponent = ({ data }) => (
        <div className="p-2 bg-light">
            <Row>
                <Col xs="12" className="mb-2">
                    <strong>Title:</strong> {data.title}
                </Col>
                <Col xs="12" className="mb-2">
                    <strong>Subtitle:</strong> {data.subtitle}
                </Col>
                <Col xs="12" className="mb-2">
                    <strong>Button Label:</strong> {data.button.label}
                </Col>
                <Col xs="12" className="mb-2">
                    <strong>Button URL:</strong>{" "}
                    <a href={data.button.actionURL} target="_blank" rel="noopener noreferrer">
                        {data.button.actionURL}
                    </a>
                </Col>
                <Col xs="12">
                    <strong>Sequence:</strong> {data.sequence || 0}
                </Col>
                <Col xs="12">
                    <strong>Status:</strong> {data.isHidden ? "Hidden" : "Visible"}
                </Col>
            </Row>
        </div>
    );

    return (
        <React.Fragment>
            <Head title="Slider List" />
            <Content>
                <BlockHead size="sm">
                    <BlockBetween>
                        <BlockHeadContent>
                            <BlockTitle tag="h3" page>
                                Slider List
                            </BlockTitle>
                            <BlockDes className="text-muted">
                                <p>You have a total of {sliders.length} sliders.</p>
                            </BlockDes>
                        </BlockHeadContent>
                        <BlockHeadContent>
                            <Row className="g-2">
                                <Col>
                                    <Button color="secondary" outline onClick={() => exportData("csv")}>
                                        <i className="bi bi-file-earmark-text me-1"></i>
                                        <span className="d-none d-md-inline">Export CSV</span>
                                    </Button>
                                </Col>
                                <Col>
                                    <Button color="secondary" outline onClick={() => exportData("xls")}>
                                        <i className="bi bi-file-earmark-excel me-1"></i>
                                        <span className="d-none d-md-inline">Export Excel</span>
                                    </Button>
                                </Col>
                                <Col>
                                    <Button color="success" onClick={() => navigate("/add-slider")}>
                                        <i className="bi bi-plus-lg me-1"></i>
                                        <span className="d-none d-md-inline">Add Slider</span>
                                    </Button>
                                </Col>
                            </Row>
                        </BlockHeadContent>
                    </BlockBetween>
                </BlockHead>
                <BlockContent>
                    <PreviewCard>
                        {loading ? (
                            <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
                                <div className="text-center">
                                    <div
                                        className="spinner-grow text-primary"
                                        style={{ width: "3rem", height: "3rem" }}
                                        role="status"
                                    >
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-3 text-muted">Loading sliders, please wait...</p>
                                </div>
                            </div>
                        ) : sliders.length === 0 ? (
                            <div className="text-center py-5">
                                <h5 className="text-muted">No sliders found</h5>
                                <Button color="primary" onClick={() => navigate("/add-slider")}>
                                    Add Your First Slider
                                </Button>
                            </div>
                        ) : (
                            <ReactDataTable
                                data={sliders}
                                columns={columns}
                                pagination
                                highlightOnHover
                                striped
                                dense
                                expandableRows
                                expandableRowsComponent={expandableRowsComponent}
                                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                className="table-responsive"
                            />
                        )}
                    </PreviewCard>
                </BlockContent>
            </Content>
            <DeleteModal
                isOpen={isDeleteModalOpen}
                toggle={toggleDeleteModal}
                onConfirm={handleDelete}
                item={selectedSlider}
                itemType="Slider"
                itemName={selectedSlider?.title}
            />
            <Modal isOpen={isSequenceModalOpen} toggle={toggleSequenceModal} centered>
                <ModalHeader toggle={toggleSequenceModal}>
                    Update Slider Sequence
                </ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label for="sequenceInput">Sequence Number</Label>
                        <Input
                            type="number"
                            id="sequenceInput"
                            value={sequenceInput}
                            min="0"
                            onChange={(e) => setSequenceInput(e.target.value)}
                            className="form-control-lg"
                            placeholder="Enter sequence number"
                        />
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleSequenceModal}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        onClick={() => handleSliderSequence(selectedSlider?._id, sequenceInput)}
                        disabled={sequenceInput < 0}
                    >
                        Update
                    </Button>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    );
};

export default SliderList;